import React, {useEffect, useState} from "react";
import {DIALOG_KEY} from "../../shared/utils/enum";
import {internalApi} from "../../common/axios";
import toast from "react-hot-toast";
import {Alert, Button, Table, Typography, Row, Col, Upload, Space} from "antd";
import {UploadOutlined, SaveOutlined, CloseOutlined, DownloadOutlined} from '@ant-design/icons';
import Papa from "papaparse";
import dayjs from "dayjs";
import {buildTemplateCsv, downloadCsv} from "./csvTemplate";

const DATE_COLUMNS = ['openDate', 'closeDate', 'begin', 'end'];

// 빈 칸은 '0/빈 문자열로 덮어쓰기'가 아니라 '미입력'으로 보고 엔티티 기본값을 쓴다. (서버 처리와 동일)
const omitBlank = (row) => Object.fromEntries(
    Object.entries(row).filter(([, value]) => String(value ?? '').trim() !== '')
);

const CsvUploadModal = ({
                            record, handleClose, dialogRef, handleSetDialog, url, entityModel,
                            identityKey: identityKeyProp, deleteMissing = true, compareUrl,
                            guide = [], csvColumns, templateName = 'csv_form.csv', checkRow
                        }) => {
    const [modifiedRecords, setModifiedRecords] = useState([]);
    const [newRecords, setNewRecords] = useState([]);
    const [deletedRecords, setDeletedRecords] = useState([]);
    const [invalidRecords, setInvalidRecords] = useState([]);
    const [fixedRecords, setFixedRecords] = useState([]);
    const [serverErrors, setServerErrors] = useState([]);
    const [isNew, setIsNew] = useState(false);
    const [baseRecords, setBaseRecords] = useState(record || []);
    const tempEntity = new entityModel();
    const identityKey = identityKeyProp || tempEntity.getIdentityKey();

    // 테이블이 서버 페이징인 경우 화면에 보이는 페이지만으로는 크로스체크가 불가능하므로 전체 데이터를 받아온다.
    useEffect(() => {
        if (!compareUrl) {
            setBaseRecords(record || []);
            return;
        }
        internalApi.get(compareUrl)
            .then((res) => setBaseRecords(res.data?.rows || []))
            .catch(() => toast.error('기존 데이터를 불러오지 못했습니다. 비교 결과가 부정확할 수 있습니다.'));
    }, [compareUrl, record]);

    const handleSave = async (e) => {
        const allRecords = [...newRecords, ...modifiedRecords.map((record) => record.data)];
        if (allRecords.length === 0 && deletedRecords.length === 0) {
            toast.error("저장할 데이터가 없습니다.");
            return;
        }
        for (const record of allRecords) {
            if (record.isInvalidate()) return;
        }
        const sanitizedData = allRecords.map(record => {
            const {errors, ...rest} = record;
            return rest;
        });
        const deletedIds = deletedRecords.map(r => r[identityKey]);
        setServerErrors([]);
        await internalApi.post(url, {data: sanitizedData, deleted: deletedIds}).then((res) => {
            toast.success(res.data.message);
            handleClose(true);
        }).catch((err) => {
            console.error(err);
            const details = err.response?.data?.details;
            setServerErrors(Array.isArray(details) ? details : [err.response?.data?.message || '저장 실패']);
        });
        handleSetDialog([{key: DIALOG_KEY.OPEN, value: false}, {key: DIALOG_KEY.CONFIRM_TEXT, value: ""}]);
    }

    const formatValue = (key, value) => {
        if (value === null || value === undefined) return '';
        // 1. 객체 또는 배열이면 직렬화(JSON 문자열 변환)
        if (typeof value === 'object') {
            return JSON.stringify(value);
        }
        // 2. 날짜 컬럼 변환
        if (DATE_COLUMNS.includes(key)) {
            const unix = Number(value);
            return unix > 0 ? dayjs.unix(unix).format('YYYY-MM-DD HH:mm:ss') : value;
        }
        return value;
    };

    const handleCSVUpload = (info) => {
        const file = info.fileList[0].originFileObj;
        if (!file || file.type !== 'text/csv') {
            toast.error('CSV(.csv) 파일만 업로드할 수 있습니다.');
            return;
        }

        Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            comments: '#',      // 양식의 설명 행(#) 은 데이터로 읽지 않는다.
            complete: (results) => {
                const baseByKey = new Map(baseRecords.map(r => [String(r[identityKey]), r]));
                const modifiedRows = [];
                const newRows = [];
                const invalidRows = [];
                const fixedRows = [];

                results.data.forEach((csvRow, idx) => {
                    const entity = new entityModel(omitBlank(csvRow));
                    const rawKey = String(csvRow[identityKey] ?? '').trim();
                    const existingRecord = rawKey === '' ? undefined : baseByKey.get(rawKey);

                    const {errors: rowErrors = [], fixes = []} = checkRow ? (checkRow(entity, csvRow) || {}) : {};
                    const messages = [
                        ...rowErrors,
                        ...(entity.errors || []).filter(rule => rule.condition()).map(rule => rule.message)
                    ];
                    const rowLabel = {line: idx + 1, key: rawKey || '(신규)'};
                    if (messages.length > 0) invalidRows.push({...rowLabel, messages});
                    if (fixes.length > 0) fixedRows.push({...rowLabel, messages: fixes});

                    if (existingRecord) {
                        const modifiedColumns = [];
                        for (const key in csvRow) {
                            if (!(key in entity) || key === identityKey) continue;

                            const oldVal = existingRecord[key];
                            const newVal = entity[key];

                            // 객체/배열은 JSON 비교, 일반 값은 != 비교
                            const isDifferent = (typeof newVal === 'object' && newVal !== null)
                                ? JSON.stringify(newVal) !== JSON.stringify(oldVal)
                                : newVal != oldVal;

                            if (isDifferent) {
                                modifiedColumns.push({
                                    column: key,
                                    oldValue: formatValue(key, oldVal),
                                    newValue: formatValue(key, newVal)
                                });
                            }
                        }
                        if (modifiedColumns.length > 0) {
                            modifiedRows.push({index: entity[identityKey], modifiedColumns, data: entity});
                        }
                    } else {
                        newRows.push(entity);
                    }
                });

                const csvRecordKeys = new Set(results.data.map(csvRecord => String(csvRecord[identityKey])));
                const recordsToDelete = deleteMissing
                    ? baseRecords.filter(r => !csvRecordKeys.has(String(r[identityKey])))
                    : [];

                setModifiedRecords(modifiedRows);
                setNewRecords(newRows);
                setDeletedRecords(recordsToDelete);
                setInvalidRecords(invalidRows);
                setFixedRecords(fixedRows);
                setServerErrors([]);

                const isRowsUpdated = newRows.length > 0 || modifiedRows.length > 0 || recordsToDelete.length > 0;
                setIsNew(isRowsUpdated);
                if (!isRowsUpdated && invalidRows.length === 0) {
                    toast.error("변경된 내용이 없습니다.");
                }
            },
            error: (error) => {
                console.error('Error parsing CSV file:', error);
                toast.error('CSV 파일을 읽지 못했습니다. 인코딩(UTF-8)과 형식을 확인해주세요.');
            }
        });
    };

    const wideTableScroll = {x: 'max-content'};
    const previewPagination = {pageSize: 50, hideOnSinglePage: true, size: 'small', showSizeChanger: false};

    const getColumns = (data) => {
        if (!data || data.length === 0) return [];
        return Object.keys(data[0]).filter(key => key !== 'errors').map(key => ({
            title: key,
            dataIndex: key,
            key,
            render: (value) => formatValue(key, value)
        }));
    };

    return (
        <Space direction="vertical" style={{width: '100%'}}>
            {guide.length > 0 && (
                <Alert
                    type="info"
                    showIcon
                    message="CSV 업로드 안내"
                    description={
                        <div style={{lineHeight: 1.7}}>
                            {guide.map((line, idx) => <div key={idx}>{line}</div>)}
                        </div>
                    }
                />
            )}

            <Space wrap>
                <Upload
                    accept=".csv"
                    showUploadList={false}
                    beforeUpload={() => false}
                    onChange={handleCSVUpload}
                >
                    <Button icon={<UploadOutlined/>}>CSV. 파일 첨부</Button>
                </Upload>
                {csvColumns?.length > 0 && (
                    <Button
                        icon={<DownloadOutlined/>}
                        onClick={() => downloadCsv(buildTemplateCsv(csvColumns), templateName)}
                    >
                        CSV 양식 다운로드
                    </Button>
                )}
            </Space>
            <span>csv를 업로드하고 바뀌거나 추가된 내용을 확인 바랍니다.</span>

            {csvColumns?.length > 0 && (
                <details>
                    <summary style={{cursor: 'pointer', fontWeight: 600}}>컬럼별 설명 ({csvColumns.length}개)</summary>
                    <Table
                        size="small"
                        dataSource={csvColumns}
                        rowKey="key"
                        pagination={false}
                        columns={[
                            {title: '컬럼', dataIndex: 'key', key: 'key', width: 160},
                            {title: '설명', dataIndex: 'desc', key: 'desc'}
                        ]}
                    />
                </details>
            )}

            {invalidRecords.length > 0 && (
                <Alert
                    type="error"
                    showIcon
                    message={`검증 오류 ${invalidRecords.length}건\n수정 후 다시 업로드해주세요.`}
                    description={
                        <div style={{maxHeight: 160, overflowY: 'auto'}}>
                            {invalidRecords.map((row, idx) => (
                                <div key={idx}>{row.line}번째 행({identityKey}: {row.key}) : {row.messages.join(' / ')}</div>
                            ))}
                        </div>
                    }
                />
            )}

            {fixedRecords.length > 0 && (
                <Alert
                    type="warning"
                    showIcon
                    message={`기본값으로 자동 보정 ${fixedRecords.length}건\n저장 전에 확인해주세요.`}
                    description={
                        <div style={{maxHeight: 160, overflowY: 'auto'}}>
                            {fixedRecords.map((row, idx) => (
                                <div key={idx}>{row.line}번째 행({identityKey}: {row.key}) : {row.messages.join(' / ')}</div>
                            ))}
                        </div>
                    }
                />
            )}

            {serverErrors.length > 0 && (
                <Alert
                    type="error"
                    showIcon
                    message="서버 검증에서 반려되었습니다."
                    description={
                        <div style={{maxHeight: 160, overflowY: 'auto'}}>
                            {serverErrors.map((line, idx) => <div key={idx}>{line}</div>)}
                        </div>
                    }
                />
            )}

            <div>
                {modifiedRecords.length > 0 && (
                    <>
                        <Typography.Text strong>수정된 데이터 ({modifiedRecords.length}건)</Typography.Text>
                        <Table
                            dataSource={modifiedRecords}
                            rowKey={(_, idx) => idx}
                            pagination={previewPagination}
                            scroll={wideTableScroll}
                            columns={[
                                {title: identityKey, dataIndex: ['data', identityKey], key: identityKey},
                                {
                                    title: 'Modified Columns',
                                    dataIndex: 'modifiedColumns',
                                    key: 'modifiedColumns',
                                    render: (cols) => (
                                        <div>
                                            {cols.map((col, idx) => (
                                                <div key={idx}>{col.column}: {col.oldValue} -&gt; {col.newValue}</div>
                                            ))}
                                        </div>
                                    )
                                }
                            ]}
                        />
                    </>
                )}
                {newRecords.length > 0 && (
                    <>
                        <Typography.Text strong>신규 데이터 ({newRecords.length}건)</Typography.Text>
                        <Table
                            dataSource={newRecords}
                            rowKey={(_, idx) => idx}
                            pagination={previewPagination}
                            scroll={wideTableScroll}
                            columns={getColumns(newRecords)}
                        />
                    </>
                )}
                {deletedRecords.length > 0 && (
                    <>
                        <Typography.Text strong>삭제될 데이터 ({deletedRecords.length}건)</Typography.Text>
                        <Table
                            dataSource={deletedRecords}
                            rowKey={(_, idx) => idx}
                            pagination={previewPagination}
                            scroll={wideTableScroll}
                            columns={getColumns(deletedRecords)}
                        />
                    </>
                )}
            </div>

            <Row justify="end" gutter={8} className="event-edit-buttons">
                <Col>
                    <Button
                        type="primary"
                        icon={<SaveOutlined/>}
                        disabled={!isNew || invalidRecords.length > 0}
                        onClick={() =>
                            handleSetDialog([
                                {key: DIALOG_KEY.OPEN, value: true},
                                {key: DIALOG_KEY.TITLE, value: "저장 확인"},
                                {
                                    key: DIALOG_KEY.BODY,
                                    value: `신규 ${newRecords.length}건 / 수정 ${modifiedRecords.length}건 / 삭제 ${deletedRecords.length}건을 반영합니다. 저장 하시겠습니까?`
                                },
                                {key: DIALOG_KEY.HANDLE, value: handleSave},
                                {key: DIALOG_KEY.BUTTON_TEXT, value: "저장"},
                                {key: DIALOG_KEY.USE_INPUT, value: false}
                            ])
                        }
                    >
                        Save
                    </Button>
                </Col>
                <Col>
                    <Button icon={<CloseOutlined/>} onClick={handleClose}>
                        Cancel
                    </Button>
                </Col>
            </Row>
        </Space>
    );
};

export default CsvUploadModal;